import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import api from '../api';
import Header from '../components/Header';
import StatsBar from '../components/StatsBar';
import PortalSidePanel from '../components/PortalSidePanel';
import s from './DashboardPage.module.scss';

const PLAN_LABELS = {
    free: 'Бесплатный', trial: 'Пробный', basic: 'Базовый',
    extended: 'Расширенный', professional: 'Профессиональный', other: 'Другой',
};

function PlanBadge({ plan }) {
    const cls = {
        free: s.bFree, trial: s.bTrial, basic: s.bBasic,
        extended: s.bExt, professional: s.bPro,
    }[plan] || s.bOther;
    return <span className={`${s.badge} ${cls}`}>{PLAN_LABELS[plan] || plan || '—'}</span>;
}

function UsageMini({ used, available }) {
    const pct = available ? Math.min((used / available) * 100, 100) : 0;
    return (
        <div className={s.usageMini}>
            <span className={s.usageNum}>{used}{available != null ? ` / ${available}` : ''}</span>
            {available != null && (
                <div className={s.miniTrack}>
                    <div className={`${s.miniFill} ${pct >= 90 ? s.miniFillWarn : ''}`} style={{ width: `${pct}%` }} />
                </div>
            )}
        </div>
    );
}

// Цикл сортировки: null → asc → desc → null
function nextSort(current, field) {
    if (!current || current.field !== field) return { field, dir: 'asc' };
    if (current.dir === 'asc') return { field, dir: 'desc' };
    return null;
}

function SortIcon({ field, sort }) {
    if (!sort || sort.field !== field) return null;
    return <span className={s.sortActive}>{sort.dir === 'asc' ? '↑' : '↓'}</span>;
}

function SortTh({ field, sort, onSort, children }) {
    return (
        <th className={s.sortable} onClick={() => onSort(field)}>
            <span className={s.thInner}>
                {children}
                <SortIcon field={field} sort={sort} />
            </span>
        </th>
    );
}

export default function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [portals, setPortals] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 200;
    const [search, setSearch] = useState('');
    const [onlyPaying, setOnlyPaying] = useState(false);
    const [onlyInstalled, setOnlyInstalled] = useState(false);
    const [sort, setSort] = useState(null); // { field, dir } | null
    const [tableLoading, setTableLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const searchTimer = useRef(null);

    useEffect(() => {
        api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
    }, []);

    const loadPortals = useCallback(async (p, q, paying, installed, sortState) => {
        setTableLoading(true);
        try {
            const params = { page: p, page_size: PAGE_SIZE, only_paying: paying, only_installed: installed };
            if (q) params.search = q;
            if (sortState) { params.sort_by = sortState.field; params.sort_dir = sortState.dir; }
            const { data } = await api.get('/admin/portals', { params });
            setPortals(data.items);
            setTotal(data.total);
        } finally {
            setTableLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPortals(page, search, onlyPaying, onlyInstalled, sort);
    }, [page, onlyPaying, onlyInstalled, sort, loadPortals]);

    const handleSearch = (val) => {
        setSearch(val);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => { setPage(1); loadPortals(1, val, onlyPaying, onlyInstalled, sort); }, 350);
    };

    const handleSort = (field) => {
        const next = nextSort(sort, field);
        setSort(next);
        setPage(1);
    };

    const handleFilter = (key, val) => {
        if (key === 'paying') { setOnlyPaying(val); setPage(1); }
        if (key === 'installed') { setOnlyInstalled(val); setPage(1); }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const refresh = () => {
        loadPortals(page, search, onlyPaying, onlyInstalled, sort);
        api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
    };

    return (
        <div className={s.root}>
            <Header />
            <div className={s.workspace}>
                <div className={s.content}>
                    <div className={s.inner}>
                        <div className={s.titleRow}>
                            <h1 className={s.title}>Порталы</h1>
                            <span className={s.count}>{total}</span>
                        </div>

                        <StatsBar stats={stats} />

                        <div className={s.toolbar}>
                            <div className={s.searchBox}>
                                <span className={s.searchIcon}>⌕</span>
                                <input
                                    className={s.searchInput}
                                    placeholder="Поиск по домену..."
                                    value={search}
                                    onChange={e => handleSearch(e.target.value)}
                                />
                                {search && <button className={s.clearBtn} onClick={() => handleSearch('')}>✕</button>}
                            </div>
                            <label className={s.checkLabel}>
                                <input type="checkbox" checked={onlyPaying} onChange={e => handleFilter('paying', e.target.checked)} />
                                Только платящие
                            </label>
                            <label className={s.checkLabel}>
                                <input type="checkbox" checked={onlyInstalled} onChange={e => handleFilter('installed', e.target.checked)} />
                                Только установленные
                            </label>
                        </div>

                        <div className={s.tableWrap}>
                            {tableLoading && <div className={s.tableLoader}><div className={s.spinner} /></div>}
                            <table className={s.table}>
                                <colgroup>
                                    <col style={{ width: '220px' }} />
                                    <col style={{ width: '105px' }} />
                                    <col style={{ width: '135px' }} />
                                    <col style={{ width: '110px' }} />
                                    <col style={{ width: '155px' }} />
                                    <col style={{ width: '175px' }} />
                                    <col style={{ width: '90px' }} />
                                    <col style={{ width: '90px' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <SortTh field="host" sort={sort} onSort={handleSort}>Хост</SortTh>
                                        <SortTh field="registered_at" sort={sort} onSort={handleSort}>Регистрация</SortTh>
                                        <SortTh field="plan_type" sort={sort} onSort={handleSort}>Тариф</SortTh>
                                        <SortTh field="subscription_end_date" sort={sort} onSort={handleSort}>Истекает</SortTh>
                                        <SortTh field="ai_requests" sort={sort} onSort={handleSort}>AI-запросы (30 дн)</SortTh>
                                        <SortTh field="extension_users" sort={sort} onSort={handleSort}>Пользователи расширения</SortTh>
                                        <th style={{ textAlign: 'center' }}>Установлено</th>
                                        <th style={{ textAlign: 'center' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {portals.length === 0 && !tableLoading ? (
                                        <tr><td colSpan={8} className={s.empty}>{search ? 'Ничего не найдено' : 'Нет данных'}</td></tr>
                                    ) : portals.map(p => (
                                        <PortalRow key={p.id} portal={p} selected={selectedId === p.id} onSelect={() => setSelectedId(p.id)} />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    </div>
                </div>

                <div className={`${s.panel} ${selectedId ? s.panelOpen : ''}`}>
                    {selectedId && <PortalSidePanel portalId={selectedId} onClose={() => setSelectedId(null)} onSaved={refresh} />}
                </div>
            </div>
        </div>
    );
}

function PortalRow({ portal, selected, onSelect }) {
    const end = portal.subscription_end_date;
    const daysLeft = end ? moment(end).diff(moment(), 'days') : null;
    const expired = daysLeft !== null && daysLeft < 0;
    const soon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;

    return (
        <tr className={`${s.row} ${selected ? s.rowSelected : ''}`}>
            <td>
                <div className={s.host}>{portal.domain}</div>
                {portal.inn && <div className={s.inn}>ИНН {portal.inn}</div>}
            </td>
            <td className={s.date}>{moment(portal.registered_at).format('DD.MM.YYYY')}</td>
            <td>{portal.plan_type ? <PlanBadge plan={portal.plan_type} /> : <span className={s.dash}>—</span>}</td>
            <td className={s.date}>
                {end ? (
                    <span className={expired ? s.expired : soon ? s.soon : ''}>
                        {moment(end).format('DD.MM.YYYY')}
                        {soon && !expired && <span className={s.dayHint}> ({daysLeft}д)</span>}
                        {expired && <span className={s.dayHint}> (истекла)</span>}
                    </span>
                ) : '—'}
            </td>
            <td><UsageMini used={portal.ai_requests.used} available={portal.ai_requests.available} /></td>
            <td><UsageMini used={portal.extension_users.used} available={portal.extension_users.available} /></td>
            <td className={s.dotCell}><span className={portal.is_installed ? s.dotGreen : s.dotRed} /></td>
            <td className={s.editCell}><button className={s.editBtn} onClick={onSelect}>Изменить</button></td>
        </tr>
    );
}
